// ---------------------------------------------------------------------------
// Alcance de las pantallas de Actividades: cliente, proyecto y repositorio.
// Puro (sin React): lo usan Tablero, Backlog y Roadmap y se prueba solo.
//
// Un ticket pertenece a un proyecto si trae su `projectId` o si su `repo` cae en
// los repos del proyecto (exactos o comodín `owner/*`). Los «clientes» son los
// proyectos que nacen de una Empresa del CRM (`source: "client"`, #973); un
// proyecto de asignación es un «proyecto». Así los mismos datos del catálogo de
// Horas sirven para los tres filtros sin pedir nada nuevo al servidor.
// ---------------------------------------------------------------------------

export interface ScopeProject {
  id: string;
  label: string;
  source?: string;
  repos?: string[];
  knownRepos?: string[];
}

export interface ScopeRow {
  projectId?: string | null;
  repo?: string | null;
}

export interface ActivityScope {
  client: string;
  project: string;
  repo: string;
}

export const EMPTY_SCOPE: ActivityScope = { client: "", project: "", repo: "" };

const lower = (v: unknown) => String(v ?? "").trim().toLowerCase();

/** ¿`repo` (owner/nombre) cae en la lista de repos (exactos o `owner/*`)? */
export function repoMatches(list: readonly string[] | undefined, repo: string | null | undefined): boolean {
  const r = lower(repo);
  if (!r || !list) return false;
  return list.some((x) => {
    const p = lower(x);
    if (p.endsWith("/*")) return r.startsWith(p.slice(0, -1));
    return p === r;
  });
}

/** ¿El ticket es del proyecto (por `projectId` o por su repo)? */
export function belongsToProject(row: ScopeRow, project: ScopeProject | undefined): boolean {
  if (!project) return false;
  if (row.projectId && lower(row.projectId) === lower(project.id)) return true;
  return repoMatches([...(project.repos ?? []), ...(project.knownRepos ?? [])], row.repo);
}

export const isClientProject = (p: ScopeProject): boolean => p.source === "client";

/** Filtra las filas según el alcance elegido. Un valor vacío no filtra. */
export function applyScope<T extends ScopeRow>(rows: readonly T[], scope: ActivityScope, projects: readonly ScopeProject[]): T[] {
  const byId = new Map(projects.map((p) => [p.id, p]));
  const client = scope.client ? byId.get(scope.client) : undefined;
  const project = scope.project ? byId.get(scope.project) : undefined;
  const repo = lower(scope.repo);
  return rows.filter((a) => {
    if (scope.client && !belongsToProject(a, client)) return false;
    if (scope.project && !belongsToProject(a, project)) return false;
    if (repo && lower(a.repo) !== repo) return false;
    return true;
  });
}

export interface ScopeOptions {
  clients: ScopeProject[];
  projects: ScopeProject[];
  repos: string[];
}

/** Opciones de los tres selectores: clientes y proyectos del catálogo, repos de las filas (y exactos de los proyectos). */
export function scopeOptions(rows: readonly ScopeRow[], projects: readonly ScopeProject[]): ScopeOptions {
  const byLabel = (a: ScopeProject, b: ScopeProject) => a.label.localeCompare(b.label);
  const repos = new Map<string, string>();
  for (const a of rows) if (a.repo) repos.set(lower(a.repo), a.repo);
  for (const p of projects) for (const r of p.repos ?? []) if (!r.endsWith("/*")) repos.set(lower(r), r);
  return {
    clients: projects.filter(isClientProject).sort(byLabel),
    projects: projects.filter((p) => !isClientProject(p)).sort(byLabel),
    repos: [...repos.values()].sort((a, b) => a.localeCompare(b)),
  };
}

export const hasScope = (s: ActivityScope): boolean => Boolean(s.client || s.project || s.repo);
